import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, ViewEncapsulation } from '@angular/core';
import { TooltipModule } from 'primeng/tooltip';
import { TASK_EVIDENCE_OUTCOME_EXPLANATIONS, TASK_EVIDENCE_OUTCOME_LABELS, TaskEvidenceOutcome } from '@jms/model';
import { BadgeComponent } from '@workbuddy/ui/components/badge';
import { HoverCardComponent } from '@workbuddy/ui/v2/components/hover.card';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { taskEvidenceOutcomeBadgeBridgeStyles, taskEvidenceOutcomeBadgeStyleId } from './task.evidence.outcome.badge.theme';

/** One outcome name and tone for the Evidence heading and each piece of evidence; nothing for a task never judged (D5). */
@Component({
  selector: 'task-evidence-outcome-badge',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [BadgeComponent, TooltipModule, HoverCardComponent],
  template: `
    @if ($outcome(); as outcome) {
      <w-badge size="small" [label]="$label()" [pTooltip]="explanation" [tooltipDisabled]="!$explanation()" [autoHide]="false" [showDelay]="200" tooltipPosition="top" tooltipStyleClass="wb-dark-tooltip" appendTo="body" />
      <ng-template #explanation><wb-hover-card [title]="$label()" [rows]="$explanationRows()" /></ng-template>
    }
  `,
  styleUrls: ['./task.evidence.outcome.badge.component.less'],
  host: {
    '[class.is-captured]': '$outcome() === TaskEvidenceOutcome.Captured',
    '[class.is-exception]': '$outcome() === TaskEvidenceOutcome.Exception',
    '[class.is-outstanding]': '$outcome() === TaskEvidenceOutcome.Outstanding || $outcome() === TaskEvidenceOutcome.CompletedWithMissingEvidence',
    '[class.is-neutral]': '$outcome() === TaskEvidenceOutcome.NoneRequired || $outcome() === TaskEvidenceOutcome.NotApplicable',
  },
})
export class TaskEvidenceOutcomeBadgeComponent {

  private readonly document = inject(DOCUMENT);

  public readonly $outcome = input<TaskEvidenceOutcome | null | undefined>(undefined, { alias: 'outcome' });
  protected readonly TaskEvidenceOutcome = TaskEvidenceOutcome;
  protected readonly $label = computed(() => {
    const outcome = this.$outcome();
    return outcome ? TASK_EVIDENCE_OUTCOME_LABELS[outcome] : '';
  });
  protected readonly $explanation = computed(() => {
    const outcome = this.$outcome();
    return outcome ? TASK_EVIDENCE_OUTCOME_EXPLANATIONS[outcome] ?? '' : '';
  });
  protected readonly $explanationRows = computed(() => [{ value: this.$explanation() }]);

  public constructor() {

    DesignSystem.Mount(this.document, taskEvidenceOutcomeBadgeStyleId, taskEvidenceOutcomeBadgeBridgeStyles);
  }
}
